import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, MailCheck, Clock, AlertOctagon, Download, Send, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

const BUCKET_META = {
  expired: { label: "EXPIRED",   color: "#FF3D00", Icon: AlertOctagon },
  "7d":    { label: "T-7 DAYS",  color: "#FF3D00", Icon: ShieldAlert },
  "30d":   { label: "T-30 DAYS", color: "#FFC400", Icon: Clock },
  "60d":   { label: "T-60 DAYS", color: "#00A8FF", Icon: Clock },
};

export default function RenewalsPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("pipeline");

  const load = useCallback(async () => {
    try {
      const [exp, hist] = await Promise.all([
        api.get("/renewals/expiring"),
        api.get("/renewals/history"),
      ]);
      setData(exp.data);
      setHistory(hist.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPipeline = async (force = false) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/renewals/run${force ? "?force=true" : ""}`);
      toast.success(`${res.created_count} reminder(s) sent · ${res.skipped_count} already on file`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const sendOne = async (asset_id) => {
    try {
      await api.post(`/renewals/send/${asset_id}`);
      toast.success("Reminder sent.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadQuote = async (asset_id, label) => {
    try {
      const res = await fetch(`${API}/renewals/quote/${asset_id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `renewal-${label}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(String(err.message || err)); }
  };

  const counts = data?.counts || { expired: 0, "7d": 0, "30d": 0, "60d": 0, total: 0 };
  const buckets = data?.buckets || { expired: [], "7d": [], "30d": [], "60d": [] };

  return (
    <div className="p-6 lg:p-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">07 · Auto-Renewal Pipeline</div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="renewals-title">{t("renewals.title")}</h1>
          <p className="mt-2 text-sm text-secondary-foreground/70">{t("renewals.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => runPipeline(false)} disabled={busy} className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50" data-testid="renewals-run-button">
            <Send className="w-4 h-4" /> {busy ? "…" : t("renewals.run")}
          </button>
          <button onClick={() => runPipeline(true)} disabled={busy} className="inline-flex items-center gap-2 px-4 h-10 rounded-sm border border-border hover:border-primary text-secondary-foreground hover:text-primary text-xs font-mono tracking-widest uppercase" data-testid="renewals-force-button">
            <RefreshCw className="w-3.5 h-3.5" /> {t("renewals.force")}
          </button>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {["expired","7d","30d","60d"].map(b => {
          const m = BUCKET_META[b];
          const Icon = m.Icon;
          return (
            <div key={b} className="p-5 border border-border rounded-sm bg-card relative overflow-hidden" data-testid={`renewals-kpi-${b}`}>
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full" style={{ background: `${m.color}15` }} />
              <div className="flex items-start justify-between">
                <Icon className="w-4 h-4" style={{ color: m.color }} strokeWidth={1.5} />
                <span className="text-[10px] font-mono tracking-[0.24em] uppercase" style={{ color: m.color }}>{m.label}</span>
              </div>
              <div className="mt-5 text-4xl font-semibold font-mono text-white">{counts[b]}</div>
              <div className="mt-1 text-[11px] font-mono tracking-widest uppercase text-muted-foreground">{t("renewals.assets")}</div>
            </div>
          );
        })}
      </section>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <TabBtn active={tab === "pipeline"} onClick={() => setTab("pipeline")} testid="renewals-tab-pipeline">{t("renewals.tab.pipeline")}</TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="renewals-tab-history">{t("renewals.tab.history")} · {history.length}</TabBtn>
      </div>

      {tab === "pipeline" ? (
        <section className="space-y-6">
          {["expired","7d","30d","60d"].map(b => buckets[b].length > 0 && (
            <BucketBlock key={b} bucket={b} rows={buckets[b]} sendOne={sendOne} downloadQuote={downloadQuote} t={t} />
          ))}
          {counts.total === 0 && (
            <div className="p-12 text-center border border-border rounded-sm bg-card text-sm text-muted-foreground" data-testid="renewals-empty">
              <MailCheck className="w-6 h-6 mx-auto mb-3 text-[#00E676]" />
              {t("renewals.empty")}
            </div>
          )}
        </section>
      ) : (
        <section className="border border-border rounded-sm bg-card overflow-hidden" data-testid="renewals-history-table">
          {history.length === 0 ? (
            <div className="p-10 text-sm text-muted-foreground text-center"><History className="w-5 h-5 mx-auto mb-2" />No reminders yet.</div>
          ) : history.map(r => (
            <div key={r.reminder_id} className="flex items-center gap-4 px-6 py-4 border-b border-border/60 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`renewals-history-${r.reminder_id}`}>
              <div className="w-1.5 h-10 rounded-sm" style={{ background: BUCKET_META[r.bucket]?.color || "#5C6A82" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-mono tracking-widest text-primary uppercase">{r.reminder_id}</div>
                <div className="text-sm text-white mt-0.5">{r.asset_id} · {r.bucket.toUpperCase()} · {r.days_remaining}d</div>
                <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-1">{r.recipient || "—"} · email: {r.email_status}</div>
              </div>
              <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase">{new Date(r.created_at).toLocaleString()}</div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 h-10 -mb-px border-b-2 text-[11px] font-mono tracking-[0.24em] uppercase transition-colors ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-secondary-foreground"
      }`}>
      {children}
    </button>
  );
}

function BucketBlock({ bucket, rows, sendOne, downloadQuote, t }) {
  const m = BUCKET_META[bucket];
  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid={`renewals-bucket-${bucket}`}>
      <div className="px-6 py-3 border-b border-border flex items-center gap-3" style={{ background: `${m.color}08` }}>
        <span className="text-[10px] font-mono tracking-[0.24em] uppercase" style={{ color: m.color }}>{m.label}</span>
        <span className="font-mono text-[11px] text-muted-foreground tracking-widest uppercase">· {rows.length} {t("renewals.assets")}</span>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">
            <th className="px-6 py-3">Asset</th>
            <th className="px-4 py-3">Site</th>
            <th className="px-4 py-3">Expiry</th>
            <th className="px-4 py-3">Renewal Quote</th>
            <th className="px-4 py-3 text-right pr-6">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.asset_id} className="border-t border-border/60 hover:bg-secondary/40 transition-colors" data-testid={`renewals-row-${r.asset_id}`}>
              <td className="px-6 py-4">
                <div className="text-sm text-white">{r.vendor} · {r.model}</div>
                <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-0.5">{r.serial}</div>
              </td>
              <td className="px-4 py-4 text-sm text-secondary-foreground">{r.site}</td>
              <td className="px-4 py-4 font-mono text-[12px]" style={{ color: m.color }}>{(r.license_expiry || "").slice(0,10)} · {r.days_remaining}d</td>
              <td className="px-4 py-4 font-mono text-[12px] text-primary">${(r.renewal_price_usd?.total || 0).toLocaleString()}</td>
              <td className="px-4 py-4 text-right pr-6">
                <div className="inline-flex items-center gap-2">
                  <button onClick={() => downloadQuote(r.asset_id, `${r.vendor}-${r.model}`)} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm border border-border hover:border-primary hover:text-primary text-[10px] font-mono tracking-widest uppercase text-secondary-foreground transition-colors" data-testid={`renewals-download-${r.asset_id}`}>
                    <Download className="w-3 h-3" /> {t("docs.download")}
                  </button>
                  <button onClick={() => sendOne(r.asset_id)} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-primary/10 border border-primary/30 hover:bg-primary/20 text-primary text-[10px] font-mono tracking-widest uppercase transition-colors" data-testid={`renewals-send-${r.asset_id}`}>
                    <Send className="w-3 h-3" /> {t("renewals.send")}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
